import { Board, Position, AIDifficulty, Mark, PlayerId } from '../types';
import { AIPlayer as IAIPlayer } from '../interfaces/AIPlayer';

export class AIPlayer implements IAIPlayer {
  /**
   * Calculates the best move for the given difficulty level
   */
  calculateMove(board: Board, difficulty: AIDifficulty): Position {
    const startTime = Date.now();
    
    let move: Position;
    
    switch (difficulty) {
      case AIDifficulty.HARD:
        move = this.getOptimalMove(board);
        break;
      case AIDifficulty.MEDIUM:
        move = this.getMediumMove(board);
        break;
      case AIDifficulty.EASY:
        move = this.getRandomMove(board);
        break;
      default:
        throw new Error(`Invalid difficulty level: ${difficulty}`);
    }
    
    // Ensure move calculation doesn't exceed 2 seconds (requirement 1.5)
    const elapsedTime = Date.now() - startTime;
    if (elapsedTime > 2000) {
      console.warn(`AI move calculation took ${elapsedTime}ms, exceeding 2 second limit`);
    }
    
    return move;
  }

  /**
   * Gets the optimal move using minimax algorithm
   */
  getOptimalMove(board: Board): Position {
    const availablePositions = this.getAvailablePositions(board);
    
    if (availablePositions.length === 0) {
      throw new Error('No available positions on the board');
    }
    
    // If it's the first move, take center or corner for efficiency
    if (availablePositions.length === 9) {
      return { row: 1, col: 1 }; // Center
    }
    
    let bestScore = -Infinity;
    let bestMove = availablePositions[0];
    
    // Determine AI's mark (assume AI is always the second player, so O)
    const aiMark = Mark.O;
    const humanMark = Mark.X;
    
    for (const position of availablePositions) {
      // Make the move
      const boardCopy = this.copyBoard(board);
      boardCopy.cells[position.row][position.col] = {
        position,
        mark: aiMark,
        playerId: 'ai' // Placeholder ID
      };
      
      // Calculate minimax score
      const score = this.minimax(boardCopy, 0, false, aiMark, humanMark);
      
      if (score > bestScore) {
        bestScore = score;
        bestMove = position;
      }
    }
    
    return bestMove;
  }

  /**
   * Gets a random valid move
   */
  getRandomMove(board: Board): Position {
    const availablePositions = this.getAvailablePositions(board);
    
    if (availablePositions.length === 0) {
      throw new Error('No available positions on the board');
    }
    
    const randomIndex = Math.floor(Math.random() * availablePositions.length);
    return availablePositions[randomIndex];
  }

  /**
   * Gets a medium difficulty move (mix of optimal and random)
   */
  getMediumMove(board: Board): Position {
    const availablePositions = this.getAvailablePositions(board);
    
    if (availablePositions.length === 0) {
      throw new Error('No available positions on the board');
    }
    
    // 70% chance to make optimal move, 30% chance to make random move
    const shouldMakeOptimalMove = Math.random() < 0.7;
    
    if (shouldMakeOptimalMove) {
      return this.getOptimalMove(board);
    } else {
      return this.getRandomMove(board);
    }
  }

  /**
   * Gets all available positions on the board
   */
  getAvailablePositions(board: Board): Position[] {
    const positions: Position[] = [];
    
    for (let row = 0; row < board.size; row++) {
      for (let col = 0; col < board.size; col++) {
        if (board.cells[row][col].mark === null) {
          positions.push({ row, col });
        }
      }
    }
    
    return positions;
  }

  /**
   * Minimax algorithm implementation
   */
  private minimax(
    board: Board, 
    depth: number, 
    isMaximizing: boolean, 
    aiMark: Mark, 
    humanMark: Mark
  ): number {
    const result = this.evaluateBoard(board, aiMark, humanMark);
    
    // Terminal states
    if (result !== null) {
      return result;
    }
    
    const availablePositions = this.getAvailablePositions(board);
    
    if (isMaximizing) {
      let maxScore = -Infinity;
      
      for (const position of availablePositions) {
        const boardCopy = this.copyBoard(board);
        boardCopy.cells[position.row][position.col] = {
          position,
          mark: aiMark,
          playerId: 'ai'
        };
        
        const score = this.minimax(boardCopy, depth + 1, false, aiMark, humanMark);
        maxScore = Math.max(score, maxScore);
      }
      
      return maxScore;
    } else {
      let minScore = Infinity;
      
      for (const position of availablePositions) {
        const boardCopy = this.copyBoard(board);
        boardCopy.cells[position.row][position.col] = {
          position,
          mark: humanMark,
          playerId: 'human'
        };
        
        const score = this.minimax(boardCopy, depth + 1, true, aiMark, humanMark);
        minScore = Math.min(score, minScore);
      }
      
      return minScore;
    }
  }

  /**
   * Evaluates the board state for minimax
   */
  private evaluateBoard(board: Board, aiMark: Mark, humanMark: Mark): number | null {
    const winner = this.checkWinner(board);
    
    if (winner === aiMark) {
      return 10; // AI wins
    } else if (winner === humanMark) {
      return -10; // Human wins
    } else if (this.getAvailablePositions(board).length === 0) {
      return 0; // Draw
    }
    
    return null; // Game continues
  }

  /**
   * Checks for a winner on the board
   */
  private checkWinner(board: Board): Mark | null {
    const cells = board.cells;
    
    // Check rows
    for (let row = 0; row < 3; row++) {
      if (cells[row][0].mark && 
          cells[row][0].mark === cells[row][1].mark && 
          cells[row][1].mark === cells[row][2].mark) {
        return cells[row][0].mark;
      }
    }
    
    // Check columns
    for (let col = 0; col < 3; col++) {
      if (cells[0][col].mark && 
          cells[0][col].mark === cells[1][col].mark && 
          cells[1][col].mark === cells[2][col].mark) {
        return cells[0][col].mark;
      }
    }
    
    // Check diagonals
    if (cells[0][0].mark && 
        cells[0][0].mark === cells[1][1].mark && 
        cells[1][1].mark === cells[2][2].mark) {
      return cells[0][0].mark;
    }
    
    if (cells[0][2].mark && 
        cells[0][2].mark === cells[1][1].mark && 
        cells[1][1].mark === cells[2][0].mark) {
      return cells[0][2].mark;
    }
    
    return null;
  }

  /**
   * Creates a deep copy of the board
   */
  private copyBoard(board: Board): Board {
    return {
      size: board.size,
      cells: board.cells.map(row => 
        row.map(cell => ({
          position: { ...cell.position },
          mark: cell.mark,
          playerId: cell.playerId
        }))
      )
    };
  }
}